import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Card, Table, Row, Col, Form, Button, Spinner, InputGroup } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";

import BreadCrumbComponent from "../../components/BreadCrumbComponent";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import AnimationLoading from "../../components/Animationloading";
import EmptyComponent from "../../components/EmptyComponent";
import PaginationsV2 from "../../components/PaginationsV2";
import { numberFormat, NewCovertTimeZone, covertTimeZone } from "../../helper";
import { getStartDate, getEndDate } from "../../components/handleDate";
import { GET_REDEEMS } from "../../apollo/redeem/Query";

export default function ReportRedeem() {
  const [activeButton, setActiveButton] = useState("thisMonth");
  const [filter, setFilter] = useState({
    createdAt_gte: getStartDate("thisMonth"),
    createdAt_lt: getEndDate("thisMonth"),
  });
  const [isCurrentModals, setIsCurrentModals] = useState(false);
  const [selectedRewardType, setSelectedRewardType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const shopId = JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;

  const [loadRedeems, { data: redeemData, loading: redeemLoading, error: redeemError }] =
    useLazyQuery(GET_REDEEMS, { fetchPolicy: "cache-and-network" });

  useEffect(() => {
    const fetchRedeems = async () => {
      if (!shopId) return;
      const skip = (currentPage - 1) * itemsPerPage;
      let where = { shop: shopId };
      if (filter?.createdAt_gte && filter?.createdAt_lt) {
        where = {
          ...where,
          createdAt_gte: NewCovertTimeZone(filter?.createdAt_gte),
          createdAt_lt: NewCovertTimeZone(filter?.createdAt_lt),
        };
      }
      if (selectedRewardType) {
        where = { ...where, rewardType: selectedRewardType };
      }
      if (selectedStatus) {
        where = { ...where, status: selectedStatus };
      }
      await loadRedeems({ variables: { where, skip, limit: itemsPerPage, orderBy: "createdAt_DESC" } });
    };
    fetchRedeems();
  }, [shopId, filter?.createdAt_gte, filter?.createdAt_lt, selectedRewardType, selectedStatus, currentPage, itemsPerPage, loadRedeems]);

  const totalItems = useMemo(() => redeemData?.redeems?.total || 0, [redeemData]);
  const pageAll = useMemo(() => Math.max(1, Math.ceil(totalItems / itemsPerPage)), [totalItems, itemsPerPage]);
  const rows = useMemo(() => redeemData?.redeems?.data || [], [redeemData]);

  const onPageChange = (pageNumber) => {
    if (pageNumber < 0 || pageNumber + 1 > pageAll) return;
    setCurrentPage(pageNumber + 1);
  };

  const filteredRows = useMemo(() => {
    if (!searchText) return rows;
    const q = searchText.toLowerCase();
    return rows.filter((r) => {
      const userName = `${r?.userConfirm?.name || ""} ${r?.userConfirm?.lastname || ""}`.toLowerCase();
      const phone = r?.userConfirm?.phone || "";
      const rewardName = r?.rewardName || r?.reward?.name || "";
      return (
        userName.includes(q) ||
        phone.includes(q) ||
        rewardName.toLowerCase().includes(q) ||
        (r?.no || "").toString().toLowerCase().includes(q)
      );
    });
  }, [rows, searchText]);

  return (
    <div style={{ padding: 10 }}>
      <BreadCrumbComponent txtSecond={"ລາຍງານການແລກລາງວັນ"} />
      <div style={{ padding: "0 1em" }}>
        <h4><b>ການແລກລາງວັນ</b></h4>
        <p>ລາຍງານການແລກລາງວັນ ຕາມຊ່ວງເວລາທີ່ເລືອກ</p>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Label htmlFor="search-input">ຄົ້ນຫາ</Form.Label>
            <InputGroup>
              <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                <SearchIcon sx={{ color: "#6B7280" }} />
              </InputGroup.Text>
              <Form.Control
                id="search-input"
                onChange={(e) => { setSearchText(e?.target?.value); setCurrentPage(1); }}
                value={searchText}
                aria-describedby="basic-addon3"
                placeholder="ຄົ້ນຫາດ້ວຍຊື່ລູກຄ້າ/ ເບີໂທ"
              />
            </InputGroup>
          </Col>
          <Col md={3}>
            <Form.Label className="filter-label">ປະເພດລາງວັນ</Form.Label>
            <Form.Select
              size="md"
            //   className="filter-select"
              value={selectedRewardType}
              onChange={(e) => { setSelectedRewardType(e.target.value); setCurrentPage(1); }}
            >
              <option value="">--ທັງຫມົດ--</option>
              <option value="DISCOUNT">ສ່ວນຫຼຸດ</option>
              <option value="ITEM">ສິນຄ້າ</option>
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="filter-label" htmlFor="date-range">ເລືອກວັນທີ</Form.Label>
            <Button
              id="date-range"
              variant="outline-secondary"
              onClick={() => setIsCurrentModals(true)}
              style={{ width: "280px", display: 'flex', gap: 5, justifyContent: 'space-between', alignItems: 'center' }}
            >
              {!filter ? (
                "mm/dd/yyyy"
              ) : (
                <>
                  {covertTimeZone(filter?.createdAt_gte) + " ຫາ " + covertTimeZone(filter?.createdAt_lt)}
                </>
              )}
            </Button>
          </Col>
        </Row>
      </div>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>ລາຍການແລກລາງວັນ</div>
        </Card.Header>
        <Card.Body>
          {redeemLoading && <AnimationLoading />}
          {redeemError && (
            <div className="text-danger">ເກີດຂໍ້ຜິດພາດໃນການດຶງຂໍ້ມູນ</div>
          )}
          {!redeemLoading && filteredRows?.length === 0 && <EmptyComponent />}

          {!redeemLoading && filteredRows?.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ເລກບິນ</th>
                    {/* <th>ວັນທີ</th> */}
                    <th>ລາງວັນ</th>
                    <th>ປະເພດ</th>
                    <th>ຄະແນນ</th>
                    <th>ລູກຄ້າ</th>
                    <th>ເບີໂທ</th>
                    <th>ພະນັກງານ</th>
                    <th>ສະຖານະ</th>
                    <th>ວັນທີທີ່ແລກ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, idx) => (
                    <tr key={row?.id || idx}>
                      <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td>{row?.orderGroup?.code || "-"}</td>
                      {/* <td>{row?.dateYmd || row?.createdAt || "-"}</td> */}
                      <td>{row?.rewardName || row?.reward?.name || "-"}</td>
                      <td>{row?.rewardType === "DISCOUNT" ? "ສ່ວນຫຼຸດ" : "ສິນຄ້າ"}</td>
                      <td>
                        {row?.point ? `ຄະແນນ ${numberFormat(row?.point)}` : row?.percent ? `ສ່ວນຫຼຸດ ${row?.percent}%` : "-"}
                      </td>
                      <td>{`${row?.userConfirm?.name || ""} ${row?.userConfirm?.lastname || ""}`.trim() || "-"}</td>
                      <td>{row?.userConfirm?.phone || "-"}</td>
                      <td>{`${row?.createdBy?.firstName || ""} ${row?.createdBy?.lastName || ""}`.trim() || "-"}</td>
                      <td>{row?.status || "-"}</td>
                      <td>{row?.createdAt ? covertTimeZone(row?.createdAt, "HH:mm:ss") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        <Card.Footer>
          <Row>
            <Col xs={12} md={6} className="d-flex align-items-center mb-2 mb-md-0">
              <div>ລວມ {numberFormat(totalItems)} ລາຍການ</div>
              <Form.Select size="sm" className="ms-2" style={{ maxWidth: 90 }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(parseInt(e.target.value) || 20); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={6}>
              <PaginationsV2
                count={totalItems}
                page={currentPage - 1}
                rowsPerPage={itemsPerPage}
                pageAll={pageAll}
                onPageChange={onPageChange}
              />
            </Col>
          </Row>
        </Card.Footer>
      </Card>

      <CurrentDateComponents
        onCloseModal={() => setIsCurrentModals(false)}
        openModal={isCurrentModals}
        setCdFillter={setFilter}
        setActiveButton={setActiveButton}
        activeButton={activeButton}
      />
    </div>
  );
}